import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { apiFetch } from "@/lib/api";
import { Button, Field, Input } from "@/components/primitives";
import { useAuth } from "@/components/auth-context";
import {
  AuthParticleCurtain,
  type AuthParticleCurtainHandle,
} from "@/components/auth-particle-curtain";
import { isPausedRegistrationMode } from "@/lib/maintenance";
import { readLastSubject } from "@/lib/subjects";
import { authPresentation, type AuthMode } from "@/lib/auth-presentation";

type Mode = AuthMode;

const MAINTENANCE_MESSAGE = "网站正在维护，普通用户暂时无法登录。管理员账号可继续登录。";

const errorMessages: Record<string, string> = {
  invalid_credentials: "邮箱或密码不正确。",
  email_already_registered: "该邮箱已经注册，请直接登录。",
  invalid_code: "验证码不正确或已过期。",
  resend_too_soon: "发送过于频繁，请稍后再试。",
  internal_error: "服务暂时不可用，请稍后重试。",
  registration_disabled: "当前暂未开放新用户注册。",
  maintenance_mode: MAINTENANCE_MESSAGE,
};

export function AuthPage({ initialMode = "login" }: { initialMode?: Mode }) {
  const { refresh, publicConfig, maintenanceEnabled, user, logout } = useAuth();
  const initiallyPausedForMaintenance = maintenanceEnabled && isPausedRegistrationMode(initialMode);
  const initiallyPausedForRegistration = publicConfig?.registrationEnabled === false && initialMode === "register";
  const [mode, setMode] = useState<Mode>(
    initiallyPausedForMaintenance || initiallyPausedForRegistration ? "login" : initialMode,
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(
    initiallyPausedForMaintenance
      ? MAINTENANCE_MESSAGE
      : initiallyPausedForRegistration
        ? "当前暂未开放新用户注册。"
        : "",
  );
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const curtainRef = useRef<AuthParticleCurtainHandle>(null);
  const registrationEnabled = (publicConfig?.registrationEnabled ?? true) && !maintenanceEnabled;
  const hasSessionCookie = document.cookie.split("; ")
    .some((entry) => entry.startsWith("pt_csrf="));

  const logoutDuringMaintenance = async () => {
    setBusy(true);
    setStatus("");
    try {
      await logout();
    } catch {
      setStatus("退出失败，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (maintenanceEnabled && isPausedRegistrationMode(mode)) {
      setMode("login");
      setStatus(MAINTENANCE_MESSAGE);
    } else if (publicConfig && !publicConfig.registrationEnabled && mode === "register") {
      setMode("login");
      setStatus("当前暂未开放新用户注册。");
    }
  }, [maintenanceEnabled, mode, publicConfig]);

  const enterApplication = async () => {
    await refresh(true);
    await curtainRef.current?.playSuccessTransition();
    await navigate({ to: "/app/$subject/dashboard", params: { subject: readLastSubject() } });
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setStatus("");
    try {
      sessionStorage.removeItem("preptrack-demo");
      if (mode === "login") {
        await apiFetch("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
        await enterApplication();
      } else if (mode === "register") {
        await apiFetch("/auth/register/request", { method: "POST", body: JSON.stringify({ email, password }) });
        setMode("verify");
        setStatus("验证码已发送，请检查邮箱。");
      } else if (mode === "verify") {
        await apiFetch("/auth/register/verify", { method: "POST", body: JSON.stringify({ email, password, code }) });
        await enterApplication();
      } else if (mode === "forgot") {
        await apiFetch("/auth/reset/request", { method: "POST", body: JSON.stringify({ email }) });
        setMode("reset");
        setStatus("若邮箱已注册，验证码会在几分钟内送达。");
      } else {
        await apiFetch("/auth/reset/confirm", { method: "POST", body: JSON.stringify({ email, password, code }) });
        setMode("login");
        setStatus("密码已更新，请重新登录。");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "request_failed";
      setStatus(errorMessages[message] ?? "暂时无法完成操作，请稍后重试。");
    } finally {
      setBusy(false);
    }
  };

  const presentation = authPresentation(mode, maintenanceEnabled, email);

  return (
    <div className="auth-layout">
      <AuthParticleCurtain ref={curtainRef} />
      <header className="auth-brand-bar">
        <Link to="/auth/login" className="auth-brand" aria-label="PrepTrack">
          <span>Prep</span><span>Track</span>
        </Link>
      </header>
      <main className="auth-panel">
        <section className="auth-form-wrap" aria-labelledby="auth-title">
          <div className="auth-heading">
            <h1 id="auth-title">{presentation.title}</h1>
            {presentation.description ? <p>{presentation.description}</p> : null}
          </div>
          <form onSubmit={submit} className="auth-form">
            <Field label="邮箱" htmlFor="email">
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} disabled={mode === "verify"} placeholder="you@example.com" />
            </Field>
            {["login", "register", "reset"].includes(mode) ? (
              <Field label={mode === "reset" ? "新密码" : "密码"} htmlFor="password" hint={mode !== "login" ? "至少 10 位，建议使用长密码。" : undefined}>
                <Input id="password" type="password" minLength={10} required value={password} onChange={(event) => setPassword(event.target.value)} autoComplete={mode === "login" ? "current-password" : "new-password"} />
              </Field>
            ) : null}
            {["verify", "reset"].includes(mode) ? (
              <Field label="邮件验证码" htmlFor="code">
                <Input id="code" inputMode="text" autoComplete="one-time-code" maxLength={8} required value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} placeholder="输入验证码" />
              </Field>
            ) : null}
            {status ? <p className="form-status" role="status">{status}</p> : null}
            <Button variant="primary" size="lg" disabled={busy} type="submit">
              {busy ? "请稍候…" : presentation.submitLabel}
            </Button>
          </form>
          <div className="auth-actions">
            {mode === "login" ? (
              <>
                {maintenanceEnabled ? <span>维护期间暂停注册</span> : registrationEnabled ? <button type="button" onClick={() => setMode("register")}>创建账户</button> : <span>暂未开放注册</span>}
                <button type="button" onClick={() => setMode("forgot")}>忘记密码？</button>
                {maintenanceEnabled && hasSessionCookie && !user?.roles.includes("ADMIN")
                  ? <button type="button" disabled={busy} onClick={() => void logoutDuringMaintenance()}>退出当前账号</button>
                  : null}
              </>
            ) : (
              <button type="button" onClick={() => setMode("login")}>返回登录</button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
